/*
  RBA colour lookup table - added 2026-09-24. Pairs with snippets/rba-colour-lookup.liquid.

  Runs only where a picker rendered [data-rba-lookup], i.e. a tee/crewneck product with no Colour
  variants. Products that still have colour variants never render it, so none of this touches them.

  - The chosen colour lives on <html> (data-rba-chosen-colour + data-rba-chosen-handle). The picker
    is re-rendered by Savor on every tier/size change, so it cannot live on the swatch.
  - A tier whose list does not include the chosen colour clears the choice, so the customer has to
    pick again instead of silently ordering a colour that tier does not come in.
  - On submit the colour is written into the form as properties[Colour]. Without a colour the
    submit is stopped.
*/
(() => {
  if (window.__rbaColourLookupReady) return;
  window.__rbaColourLookupReady = true;

  const root = document.documentElement;

  const chosenColour = (picker) =>
    root.dataset.rbaChosenHandle === picker.dataset.productHandle ? root.dataset.rbaChosenColour || '' : '';

  const clearColourChoice = () => {
    delete root.dataset.rbaChosenColour;
    delete root.dataset.rbaColourChosen;
    delete root.dataset.rbaAllChosen;
  };

  const stopWatcher = (picker, key) => {
    const watcher = picker[key];
    if (!watcher) return;
    window.clearInterval(watcher.interval);
    watcher.observer?.disconnect();
    picker[key] = null;
  };

  const galleryFor = (picker) => picker.closest('.shopify-section')?.querySelector('media-gallery');

  const hidePreview = (picker) => {
    galleryFor(picker)?.querySelector('.rba-lookup-preview')?.remove();
    picker._rbaLookupPreview?.remove();
    picker._rbaLookupPreview = null;
  };

  const selectSlide = (picker, mediaId) => {
    const slideshow = galleryFor(picker)?.slideshow;
    const slides = slideshow?.slides;
    if (!slideshow || !slides?.length) return false;

    const index = slides.findIndex((slide) => slide.querySelector(`.product-media[data-media-id="${mediaId}"]`));
    if (index < 0) return false;

    const current = typeof slideshow.selected === 'number' ? slideshow.selected : slideshow.current;
    if (current !== index) slideshow.select(index, undefined, { animate: false });
    return true;
  };

  // Hold the colour's slide for a moment: Savor's own variant handling can move the gallery a frame
  // after the click, the same race the picker's lockRbaColourMedia guards against.
  const holdSlide = (picker, mediaId) => {
    const stopAt = performance.now() + 2200;
    const tick = () => {
      if (picker.dataset.rbaLastInteraction !== 'colour' || performance.now() >= stopAt) return;
      selectSlide(picker, mediaId);
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  };

  const placePreview = (gallery, img) => {
    const slide = gallery?.querySelector('slideshow-slide[aria-hidden="false"]') || gallery?.querySelector('slideshow-slide');
    if (!slide) return false;
    if (img.parentElement !== slide) {
      if (getComputedStyle(slide).position === 'static') slide.style.position = 'relative';
      slide.append(img);
    }
    return true;
  };

  const showPreview = (picker, src, alt) => {
    const gallery = galleryFor(picker);
    const img = picker._rbaLookupPreview || gallery?.querySelector('.rba-lookup-preview') || document.createElement('img');
    img.className = 'rba-lookup-preview';
    if (!placePreview(gallery, img)) return;
    img.alt = alt;
    if (img.getAttribute('src') !== src) img.src = src;
    // Kept on the picker so the same, already loaded image can go straight back in when the
    // gallery is replaced (see watchGallery).
    picker._rbaLookupPreview = img;
  };

  // On the crewneck, Savor swaps in a brand-new <media-gallery> on every variant change (size
  // included), which throws the overlay away and shows the mockup until the fetch handler below puts
  // it back: the flash Sean saw on 28 Sep. A MutationObserver runs in the same task as the swap,
  // before the browser paints, so re-inserting the same image element here leaves no mockup frame.
  const watchGallery = (picker) => {
    const section = picker.closest('.shopify-section');
    if (picker._rbaGalleryObserver || !section || !picker.querySelector('[data-rba-lookup]')) return;
    const observer = new MutationObserver(() => {
      const img = picker._rbaLookupPreview;
      if (!img || !chosenColour(picker)) return;
      const gallery = galleryFor(picker);
      if (gallery && !gallery.contains(img)) placePreview(gallery, img);
    });
    observer.observe(section, { childList: true, subtree: true });
    picker._rbaGalleryObserver = observer;
  };

  // Both pickers re-select `rbaPendingMediaId` after every variant change (as a native colour click
  // sets it), so it must point at the slide showing the colour, or a size change snaps back to the mockup.
  const showColourImage = (picker, swatch) => {
    const mediaId = swatch.dataset.mediaId;
    if (mediaId && selectSlide(picker, mediaId)) {
      picker.dataset.rbaPendingMediaId = mediaId;
      hidePreview(picker);
      holdSlide(picker, mediaId);
      return;
    }
    if (swatch.dataset.rbaLookupImage) {
      showPreview(picker, swatch.dataset.rbaLookupImage, swatch.title);
      const host = galleryFor(picker)?.querySelector('.rba-lookup-preview')?.parentElement;
      const hostMediaId = host?.querySelector('.product-media')?.dataset.mediaId;
      if (hostMediaId) picker.dataset.rbaPendingMediaId = hostMediaId;
    }
  };

  const updateSummary = (picker, colour) => {
    const summary = picker.closest('.shopify-section')?.querySelector('.rba-selection-summary [data-rba-selection-text]');
    if (!summary) return;

    summary.querySelector('[data-rba-part="colour"]')?.remove();
    if (!colour) return;

    const part = document.createElement('span');
    part.dataset.rbaPart = 'colour';
    part.textContent = ` · ${colour}`;
    const prompt = summary.querySelector('[data-rba-prompt="colour"]');
    summary.insertBefore(part, prompt || null);
  };

  // Savor's Add to Cart does not fire a submit event: the button's own click handler reads
  // `new FormData(form)` directly. So the Colour property is kept in the form at all times, in every
  // cart form of this product (there are two; only the one inside <product-form-component> is sent).
  const setColourProperty = (picker, colour) => {
    const details = picker.closest('.product-details');
    details?.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      let input = form.querySelector('input[name="properties[Colour]"]');
      if (!colour) {
        input?.remove();
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Colour]';
        form.append(input);
      }
      if (input.value !== colour) input.value = colour;
    });
  };

  // Re-applies the chosen colour to freshly rendered markup.
  const sync = (picker) => {
    const box = picker.querySelector('[data-rba-lookup]');
    if (!box) return;

    if (root.dataset.rbaChosenHandle !== picker.dataset.productHandle) {
      root.dataset.rbaChosenHandle = picker.dataset.productHandle || '';
      delete root.dataset.rbaChosenColour;
    }

    const colour = chosenColour(picker);
    let match = null;
    box.querySelectorAll('[data-rba-lookup-colour]').forEach((swatch) => {
      const on = Boolean(colour) && swatch.dataset.rbaLookupColour === colour;
      // Only write when it differs: the observer below watches this attribute.
      const pressed = on ? 'true' : 'false';
      if (swatch.getAttribute('aria-pressed') !== pressed) swatch.setAttribute('aria-pressed', pressed);
      if (on) match = swatch;
    });

    if (colour && !match) {
      clearColourChoice();
      hidePreview(picker);
    }

    const label = box.querySelector('[data-rba-lookup-label]');
    const labelText = match ? colour : '';
    if (label && label.textContent !== labelText) label.textContent = labelText;
    updateSummary(picker, labelText);
    setColourProperty(picker, labelText);
  };

  // Savor morphs the picker after the variant fetch, which resets aria-pressed and the label to the
  // server markup. Re-apply after every morph rather than guessing when it has finished.
  const watchPicker = (picker) => {
    if (picker._rbaLookupObserver || !picker.querySelector('[data-rba-lookup]')) return;
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      // setTimeout, not requestAnimationFrame: rAF never fires in a hidden tab.
      window.setTimeout(() => {
        queued = false;
        sync(picker);
      }, 0);
    });
    observer.observe(picker, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-pressed', 'data-rba-lookup-tier'],
    });
    picker._rbaLookupObserver = observer;
  };

  const syncAll = () =>
    document.querySelectorAll('.rba-tee-picker').forEach((picker) => {
      sync(picker);
      watchPicker(picker);
      watchGallery(picker);
    });

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('[data-rba-lookup-colour]');
    if (!swatch) {
      // A tier click resets the gallery to the mockup, so the colour overlay goes too.
      const tierCard = event.target.closest?.('.rba-tier-card');
      const picker = tierCard?.closest('.rba-tee-picker');
      const box = picker?.querySelector('[data-rba-lookup]');
      if (box) {
        hidePreview(picker);
        // Until the new tier's colour list arrives we cannot tell whether the chosen colour exists
        // in it, so an Add clicked meanwhile is held (see guardAddToCart).
        const tier = tierCard.querySelector('input')?.value;
        if (tier && tier !== box.dataset.rbaLookupTier) picker._rbaTierPendingUntil = performance.now() + 4000;
      }
      return;
    }

    const picker = swatch.closest('.rba-tee-picker');
    if (!picker) return;

    root.dataset.rbaChosenHandle = picker.dataset.productHandle || '';
    root.dataset.rbaChosenColour = swatch.dataset.rbaLookupColour;
    root.dataset.rbaColourChosen = 'true';
    if (root.dataset.rbaTierChosen === 'true' && root.dataset.rbaSizeChosen === 'true') {
      root.dataset.rbaAllChosen = 'true';
    }

    picker.dataset.rbaLastInteraction = 'colour';
    stopWatcher(picker, '_rbaSingleMockupWatcher');
    stopWatcher(picker, '_rbaTierWatcher');
    picker.querySelector('[data-rba-lookup]')?.classList.remove('rba-lookup--missing');

    sync(picker);
    showColourImage(picker, swatch);
  });

  document.addEventListener('shopify:product:select', (event) => {
    const picker = event.target?.closest?.('.rba-tee-picker');
    if (!picker) return;
    Promise.resolve(event.promise)
      .then(() =>
        window.setTimeout(() => {
          sync(picker);
          // The new tier's colours are in: replay an Add held during the tier change. The guard runs
          // again on this click, so a colour the new tier lacks asks for a colour instead.
          picker._rbaTierPendingUntil = 0;
          if (picker._rbaHeldAdd) {
            picker._rbaHeldAdd = false;
            picker.closest('.product-details')?.querySelector('product-form-component button[name="add"]')?.click();
          }
          // Savor moves (and on the crewneck re-renders) the gallery after a variant change, which
          // moves off the colour's slide or drops the overlay. Put the colour image back.
          if (picker.dataset.rbaLastInteraction !== 'colour') return;
          const colour = chosenColour(picker);
          const swatch = colour
            ? [...picker.querySelectorAll('button[data-rba-lookup-colour]')].find(
                (button) => button.dataset.rbaLookupColour === colour
              )
            : null;
          if (swatch) showColourImage(picker, swatch);
        }, 50)
      )
      .catch(() => {});
  });

  // Stops an add to cart without a colour. Runs in the capture phase so it gets in before Savor's own
  // click handler on the button (which never fires a submit event), and again on submit as a fallback.
  const guardAddToCart = (event, details) => {
    const box = details?.querySelector('[data-rba-lookup]');
    const picker = box?.closest('.rba-tee-picker');
    if (!box || !picker) return;

    if (picker._rbaTierPendingUntil && performance.now() < picker._rbaTierPendingUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
      picker._rbaHeldAdd = true;
      return;
    }

    const colour = chosenColour(picker);
    const offered = colour && [...box.querySelectorAll('[data-rba-lookup-colour]')].some(
      (swatch) => swatch.dataset.rbaLookupColour === colour
    );

    if (!offered) {
      event.preventDefault();
      event.stopImmediatePropagation();
      box.classList.add('rba-lookup--missing');
      box.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setColourProperty(picker, colour);
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[name="add"]');
      if (button) guardAddToCart(event, button.closest('.product-details'));
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form?.matches?.('form[action*="/cart/add"]')) guardAddToCart(event, form.closest('.product-details'));
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll, { once: true });
  } else {
    syncAll();
  }
  document.addEventListener('shopify:section:load', syncAll);
})();
